import type { Component } from '../schema';
import type { SelectionsMap } from '../state/selections';
import { effectiveSelections } from '../state/effectiveSelections';
import { evaluateAll } from '../eval/evaluator';
import type { EvaluatedMap } from '../eval/types';
import { priceLinesFor } from './sources';
import { hostPriceIncludesTax, resolveCurrency, toHostAmount, type HostPricing } from './host';
import type { PriceBreakdown, PriceLine } from './types';

/**
 * Entry-point: produce the {@link PriceBreakdown} for a Component given the
 * current Selections. Pure function — same inputs always yield an output
 * that compares equal by deep equality.
 *
 * Optionally accepts a pre-computed {@link EvaluatedMap} so callers that
 * already evaluated calculated characteristics (e.g. the UI render path
 * uses the map to display live values) can avoid recomputing. If omitted
 * the evaluator is run inline.
 */
export interface CalculatePriceOptions {
    /**
     * Tax rate (percent) of the embedding host shop. When provided, every line
     * + total is rebased from the configurator's tax basis onto the host's
     * basis (see {@link convertTax}). Omit for standalone preview.
     */
    storeTaxRate?: number | undefined;
    /** Currency the host displays; see {@link HostPricing.displayCurrency}. */
    displayCurrency?: string | undefined;
    /** Host conversion factors; see {@link HostPricing.currencyRates}. */
    currencyRates?: Readonly<Record<string, number>> | undefined;
    /**
     * Characteristic IDs hidden by an active rule. Excluded from both the
     * line list AND the running total — a hidden characteristic's value
     * doesn't count toward the cart price either.
     */
    hiddenCharacteristics?: ReadonlySet<number> | undefined;
    /**
     * Value IDs that an active rule has blacklisted. Removed from
     * selections before pricing so a stale predefine pointing at a
     * blacklisted value doesn't keep contributing to the total after
     * the UI has stopped offering it.
     */
    blacklistedValues?: ReadonlySet<number> | undefined;
}

export function calculatePrice(
    component: Component,
    selections: SelectionsMap,
    evaluated?: EvaluatedMap,
    options?: CalculatePriceOptions,
): PriceBreakdown {
    const evalMap = evaluated ?? evaluateAll(component, selections);
    const hidden = options?.hiddenCharacteristics ?? new Set<number>();
    const blacklisted = options?.blacklistedValues ?? new Set<number>();
    // Only rule-blacklisted characteristics drop out of the total.
    // `visibility = hidden` / `backend` means "don't render in the UI" —
    // a typical use is a pinned base-image with a fixed price that the
    // customer never sees individually but DOES pay for. Filtering them
    // out of the total would zero out exactly the kind of admin pricing
    // those flags are designed for.
    const allChars = component.groups
        .flatMap((g) => g.characteristics)
        .filter((c) => !hidden.has(c.id));

    // Scrub selections: a value the rule engine has just blacklisted (or
    // a value that was already removed from the wire format) must not
    // contribute to the total — the UI already drops it from the option
    // list, the pricing path has to follow. Callers that went through
    // `deriveConfiguration` hand us an already-effective map; the call is
    // idempotent, so it stays a backstop for direct callers and tests.
    const sanitizedSelections = effectiveSelections(selections, {
        hiddenCharacteristics: hidden,
        blacklistedValues: blacklisted,
    });

    const configuratorTaxRate = parseTaxRate(component.setting.taxRate);
    const storeTaxRate = options?.storeTaxRate;
    const host: HostPricing = {
        storeTaxRate,
        displayCurrency: options?.displayCurrency,
        currencyRates: options?.currencyRates,
    };
    const resolvedCurrency = resolveCurrency(component.setting.defaultCurrencyCode ?? 'EUR', host);
    // Any host rate rebases — also an equal one: a net-authored Component in
    // a gross shop at the same rate still has to gain the tax (Legacy
    // `App_Tax.convert` semantics). convertTax keeps gross→gross at an equal
    // rate bit-identical.
    const needsConvert =
        (storeTaxRate !== undefined && Number.isFinite(storeTaxRate)) || resolvedCurrency.rate !== 1;
    const convert = (amount: number) =>
        needsConvert ? toHostAmount(amount, component, host, resolvedCurrency) : amount;

    const allLines: PriceLine[] = [];
    let total = 0;
    for (const char of allChars) {
        for (const line of priceLinesFor(char, sanitizedSelections, evalMap, component)) {
            const converted = needsConvert ? { ...line, amount: convert(line.amount) } : line;
            allLines.push(converted);
            total += converted.amount;
        }
    }

    const currency = resolvedCurrency.currency;
    // Reported rate and flag follow the basis the totals are denominated in:
    // the host's when it named one, otherwise the configurator's own.
    const hasHostTax = storeTaxRate !== undefined && Number.isFinite(storeTaxRate);
    const taxRatePercent = hasHostTax ? (storeTaxRate ?? 0) : configuratorTaxRate;

    // Compress lines: only show non-zero amounts AND lines that carry a Value
    // (so users still see "Picked: Red" with €0 contribution).
    const visibleLines = allLines.filter((l) => l.amount !== 0 || l.value !== undefined);

    return {
        total,
        currency,
        priceIncludesTax: hostPriceIncludesTax(component, host),
        taxRatePercent,
        currencyRate: resolvedCurrency.rate,
        storeTaxRate: hasHostTax ? storeTaxRate : undefined,
        lines: visibleLines,
    };
}

function parseTaxRate(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
}
